import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowLeft,
  FiExternalLink,
  FiEdit3,
  FiCalendar,
  FiHeart,
  FiCpu,
  FiLayout,
  FiShield,
  FiArrowRight,
} from 'react-icons/fi';
import SEO from '../../components/SEO';
import './CaseStudy.css';

/**
 * GraceToChangeCaseStudy — /portfolio/gracetochange. Same CaseStudy
 * .css scaffolding as the others; accent is the client's burnt-orange
 * brand color and copy leads with the WordPress → custom-CMS rebuild
 * and the $100K post-launch grant outcome.
 */
const ACCENT = '#C0603F';
const LIVE_URL = 'https://gracetochange.org/';
const IMG_MAC =
  'https://silverfoxmedia.s3.amazonaws.com/GTCnewsitehomepage2-front.png';

const GraceToChangeCaseStudy = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Grace to Change — Case Study',
    headline: 'Non-Profit Website + Custom Admin CMS',
    description:
      "Full rebuild for a North Texas addiction-recovery nonprofit — WordPress replaced with a fast public site and a custom admin CMS the team runs themselves. Client received a $100K grant from the City of McKinney after launch.",
    image: IMG_MAC,
    url: 'https://silverfoxmedia.co/portfolio/gracetochange',
    author: {
      '@type': 'Organization',
      name: 'SilverFox Media',
      url: 'https://silverfoxmedia.co',
    },
    about: [
      { '@type': 'Thing', name: 'Non-Profit Website' },
      { '@type': 'Thing', name: 'Custom Admin CMS' },
      { '@type': 'Thing', name: 'AI Blog Generation' },
      { '@type': 'Thing', name: 'Anthropic Claude' },
    ],
  };

  return (
    <>
      <SEO
        title="Grace to Change Case Study: Non-Profit Website + Custom Admin CMS"
        description="How SilverFox Media rebuilt Grace to Change: a North Texas addiction-recovery nonprofit, moved off WordPress onto a custom Node + React admin CMS with events management, volunteer opportunities, and an AI blog composer. Received a $100K post-launch grant."
        keywords="Grace to Change case study, non-profit website rebuild, custom admin CMS, MERN stack non-profit, AI blog composer, Anthropic Claude, events management, volunteer management, McKinney Texas"
        image="https://silverfoxmedia.co/images/og/gracetochange.jpg"
        url="/portfolio/gracetochange"
        type="article"
        schema={structuredData}
      />

      <article
        className="case-study"
        style={{ '--case-study-accent': ACCENT }}
      >
        {/* HERO */}
        <section className="case-study__hero">
          <div className="container">
            <div className="case-study__hero-grid">
              <div className="case-study__hero-inner">
                <Link to="/portfolio" className="case-study__back">
                  <FiArrowLeft />
                  Back to Portfolio
                </Link>

                <span className="case-study__category">Non-Profit</span>

                <h1 className="case-study__title">
                  Grace to Change:{' '}
                  <span className="case-study__title-accent">
                    A Nonprofit CMS the Team Runs Themselves
                  </span>
                </h1>

                <p className="case-study__tagline">
                  A full rebuild for a North Texas addiction-recovery nonprofit —
                  replaced their aging WordPress site with a fast, mobile-first
                  public site and a custom admin dashboard so the team can
                  publish events, volunteer opportunities, and AI-drafted blog
                  posts without calling a developer. Received a $100K grant from
                  the City of McKinney after launch.
                </p>

                <div className="case-study__hero-cta">
                  <a
                    href={LIVE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="case-study__cta-primary"
                  >
                    Visit Live Site <FiExternalLink />
                  </a>
                  <Link to="/contact" className="case-study__cta-secondary">
                    Start Your Project <FiArrowRight />
                  </Link>
                </div>

                <div className="case-study__hero-meta">
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Client</span>
                    <span className="case-study__hero-meta-value">Grace to Change</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Industry</span>
                    <span className="case-study__hero-meta-value">Non-Profit / Addiction Recovery</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Timeline</span>
                    <span className="case-study__hero-meta-value">2026 — Live</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Role</span>
                    <span className="case-study__hero-meta-value">Full-Stack Rebuild</span>
                  </div>
                </div>
              </div>

              <div className="case-study__hero-media">
                <img
                  src={IMG_MAC}
                  alt="Grace to Change homepage displayed on a MacBook mockup"
                  className="case-study__hero-macbook"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* FEATURED IMAGE */}
        <section className="case-study__featured">
          <div className="container">
            <div className="case-study__featured-image-wrap">
              <img
                src={IMG_MAC}
                alt="Grace to Change homepage displayed on a MacBook mockup"
                className="case-study__featured-image"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* OVERVIEW */}
        <section className="case-study__section">
          <div className="container">
            <p className="case-study__section-label">The Overview</p>
            <h2 className="case-study__section-title">
              A public site that welcomes, and an admin panel the team owns.
            </h2>
            <div className="case-study__prose">
              <p>
                Grace to Change is a North Texas nonprofit walking alongside
                individuals and families through addiction recovery. Their old
                WordPress site had aged into slow load times, plugin sprawl, and
                a content workflow that meant every update went through a
                developer. Board members would email edits to whoever was on
                call. Blog posts sat unpublished for weeks.
              </p>
              <p>
                We rebuilt the whole site from the ground up. The public side is
                a fast, mobile-first storytelling site — About, Programs, Events,
                Volunteer, Blog, Donate — with real search-engine optimization
                built into every post. The admin side is a custom dashboard the
                team runs themselves: create events, manage volunteer
                opportunities by cadence, and draft blog posts with an
                AI-assisted composer. No more calling the developer for a typo.
              </p>
            </div>
          </div>
        </section>

        {/* CHALLENGE */}
        <section className="case-study__section" style={{ background: 'var(--color-gray-100)' }}>
          <div className="container">
            <p className="case-study__section-label">The Challenge</p>
            <h2 className="case-study__section-title">
              A small team, big content needs, and a website that shouldn't slow them down.
            </h2>
            <div className="case-study__prose">
              <p>
                Nonprofits live and die by content velocity. Events are the top
                of the funnel for donations, volunteers show up because the site
                surfaced the right opportunity at the right time, and blog posts
                are the SEO engine that brings new families to the program.
                Whatever we built had to make publishing all three fast enough
                that the team would actually do it — not queue it up as a
                "someday" task.
              </p>
              <p>
                The team is small. There's no dedicated web person. Anyone on
                staff or the board should be able to open the admin, add an
                event, upload a photo, and publish — without breaking anything,
                without learning HTML, without needing training every quarter.
                Every admin surface had to be forgiving, obvious, and undo-able.
              </p>
              <p>
                And the whole thing had to be fast, mobile-first, and
                search-engine friendly on day one. People finding this nonprofit
                are often in crisis — the site can't take three seconds to load,
                can't hide the phone number, and can't rely on JavaScript to
                render the first paint.
              </p>
            </div>
          </div>
        </section>

        {/* SOLUTION / KEY FEATURES */}
        <section className="case-study__section">
          <div className="container">
            <p className="case-study__section-label">The Solution</p>
            <h2 className="case-study__section-title">
              A custom Node + React admin that treats content as a first-class product.
            </h2>
            <p className="case-study__section-lead">
              We moved the site off WordPress entirely. Public pages are static
              HTML served fast from a single Node service; the admin is a React
              SPA with real authentication; blog posts render server-side from
              Mongo with JSON-LD structured data baked in.
            </p>

            <div className="case-study__features">
              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiLayout /></span>
                <h3 className="case-study__feature-title">Custom Admin Dashboard</h3>
                <p className="case-study__feature-body">
                  Real auth (httpOnly session cookies, bcrypt password hashing,
                  MongoDB-backed sessions). Live stats + recent activity feed
                  as the landing screen so the team sees what's happening at a
                  glance.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiCalendar /></span>
                <h3 className="case-study__feature-title">Events Management</h3>
                <p className="case-study__feature-body">
                  Full CRUD for events with S3 image uploads via signed URLs,
                  per-row pause / publish toggle, and an admin-picked "featured
                  event" that surfaces on the homepage banner. Team publishes
                  new events without touching code.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiHeart /></span>
                <h3 className="case-study__feature-title">Volunteer Opportunities</h3>
                <p className="case-study__feature-body">
                  Volunteer cards grouped by cadence — weekly, monthly, yearly —
                  with a preset SVG icon library so every card stays on-brand.
                  Pause / publish per opportunity, sortable ordering, and a
                  configurable CTA button per card.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiCpu /></span>
                <h3 className="case-study__feature-title">AI Blog Composer</h3>
                <p className="case-study__feature-body">
                  Admin enters keywords and uploads a hero image; Anthropic
                  Claude drafts the full post — headline, meta description,
                  semantic HTML body, FAQ items, and related-link picks — in a
                  compassionate nonprofit voice. Team reviews, edits, publishes.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiEdit3 /></span>
                <h3 className="case-study__feature-title">Server-Side Blog Rendering</h3>
                <p className="case-study__feature-body">
                  Blog posts render server-side from Mongo with SEO JSON-LD
                  (BlogPosting + FAQPage schemas) baked into every response.
                  No JavaScript required to render the first paint — Google
                  indexes it, users on slow connections still see it.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiShield /></span>
                <h3 className="case-study__feature-title">Single Render Deploy</h3>
                <p className="case-study__feature-body">
                  One Node service on Render serves the API, the admin SPA, and
                  the public static pages. No CORS overhead, no separate hosts,
                  one deploy pipeline. Simple to operate, easy to hand off.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TECH STACK */}
        <section className="case-study__section" style={{ background: 'var(--color-gray-100)' }}>
          <div className="container">
            <p className="case-study__section-label">The Stack</p>
            <h2 className="case-study__section-title">Node + Express + React admin, MongoDB + S3, Claude for content.</h2>
            <p className="case-study__section-lead">
              Purpose-picked stack. No CMS bloat, no plugin marketplace to
              maintain, no theme framework to keep patched.
            </p>

            <div className="case-study__tech">
              <span className="case-study__tech-chip">Node.js</span>
              <span className="case-study__tech-chip">Express</span>
              <span className="case-study__tech-chip">React 18</span>
              <span className="case-study__tech-chip">Vite</span>
              <span className="case-study__tech-chip">MongoDB</span>
              <span className="case-study__tech-chip">Mongoose</span>
              <span className="case-study__tech-chip">Anthropic Claude</span>
              <span className="case-study__tech-chip">AWS S3</span>
              <span className="case-study__tech-chip">Qgiv (Donations)</span>
              <span className="case-study__tech-chip">SendGrid</span>
              <span className="case-study__tech-chip">bcryptjs</span>
              <span className="case-study__tech-chip">Render</span>
            </div>
          </div>
        </section>

        {/* RESULTS */}
        <section className="case-study__section case-study__section--dark">
          <div className="container">
            <p className="case-study__section-label">The Results</p>
            <h2 className="case-study__section-title">Shipped, indexed, and immediately awarded a $100K grant.</h2>
            <p className="case-study__section-lead" style={{ color: 'rgba(255,255,255,0.75)' }}>
              Grace to Change is live at gracetochange.org. The team publishes
              their own events, opportunities, and blog posts. And the launch
              itself was strong enough to land a six-figure grant from the local
              city government.
            </p>

            <div className="case-study__stats">
              <div className="case-study__stat">
                <p className="case-study__stat-value">$100K</p>
                <p className="case-study__stat-label">City of McKinney grant received after launch</p>
              </div>
              <div className="case-study__stat">
                <p className="case-study__stat-value">0</p>
                <p className="case-study__stat-label">Developer calls needed to publish new content</p>
              </div>
              <div className="case-study__stat">
                <p className="case-study__stat-value">1 team</p>
                <p className="case-study__stat-label">Board + staff can update the whole site themselves</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="case-study__section">
          <div className="container">
            <div className="case-study__cta">
              <h2 className="case-study__cta-title">Have a non-profit build in mind?</h2>
              <p className="case-study__cta-text">
                Whether you need a custom admin your team can actually use, an
                AI-assisted content workflow, or a full rebuild off WordPress —
                we build non-profit platforms end-to-end. Let's talk.
              </p>
              <Link to="/contact" className="case-study__cta-btn">
                Start Your Project <FiArrowRight />
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default GraceToChangeCaseStudy;
